import type { IChatMessage } from "@/types/(my-space)/chat.interface";

/**
 * "Jump to my mentions": the unread messages that tag me, oldest first.
 *
 * Seeded ONCE per room from the first conversation detail
 * (`unreadMentionIds`, computed from my read pointer before this visit marked
 * anything read); later refetches would return [] and must not overwrite it.
 * Live tags I'd otherwise miss are added; a jump consumes the oldest; a tag
 * that scrolls into view is removed.
 */
export interface MentionQueue {
  ids: number[];
  seeded: boolean;
}

export const EMPTY_MENTION_QUEUE: MentionQueue = { ids: [], seeded: false };

const sortedUnique = (ids: number[]) =>
  [...new Set(ids.filter((id) => id > 0))].sort((a, b) => a - b);

/** First seed wins; ids queued live before it arrived are kept. */
export const seedMentionQueue = (q: MentionQueue, ids: readonly number[]): MentionQueue =>
  q.seeded ? q : { ids: sortedUnique([...q.ids, ...ids]), seeded: true };

export const addMention = (q: MentionQueue, id: number): MentionQueue =>
  id <= 0 || q.ids.includes(id) ? q : { ...q, ids: sortedUnique([...q.ids, id]) };

export const removeMentions = (q: MentionQueue, ids: Iterable<number>): MentionQueue => {
  const drop = new Set(ids);
  if (!q.ids.some((id) => drop.has(id))) return q;
  return { ...q, ids: q.ids.filter((id) => !drop.has(id)) };
};

/** The oldest queued id (null when empty) and the queue without it. */
export const consumeOldestMention = (
  q: MentionQueue,
): { id: number | null; queue: MentionQueue } =>
  q.ids.length ? { id: q.ids[0], queue: { ...q, ids: q.ids.slice(1) } } : { id: null, queue: q };

/** Someone else's live message that tags me. */
export const tagsMe = (m: IChatMessage, myUserId: number | null | undefined) =>
  myUserId != null &&
  m.id > 0 &&
  !m.isDeleted &&
  m.sender?.id !== myUserId &&
  !!m.metadata?.mentions?.some((x) => x.userId === myUserId);

/** Share of a bubble (or of the viewport, for a bubble taller than it) that counts as read. */
export const MENTION_SEEN_RATIO = 0.6;

/**
 * Whether an observed bubble has been seen: 60% of it on screen — or, for a
 * bubble too tall to ever be 60% visible, 60% of the viewport filled by it.
 */
export const isMentionSeen = (e: {
  isIntersecting: boolean;
  intersectionRatio: number;
  visibleHeight: number;
  rootHeight: number | null;
}) =>
  e.isIntersecting &&
  (e.intersectionRatio >= MENTION_SEEN_RATIO ||
    (e.rootHeight != null && e.rootHeight > 0 && e.visibleHeight >= e.rootHeight * MENTION_SEEN_RATIO));

/**
 * This visit's first conversation detail has resolved: a response fulfilled
 * at or after the room opened (a cached copy from an earlier visit fulfilled
 * before), or a failure — reading must not wait on a detail that never comes.
 */
export const firstDetailResolved = (q: {
  fulfilledTimeStamp?: number;
  openedAt: number;
  isError: boolean;
  isFetching: boolean;
}) => (q.fulfilledTimeStamp ?? 0) >= q.openedAt || (q.isError && !q.isFetching);
